import React, { useState, useEffect, memo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import memoize from 'memoize-one';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';

import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';

import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

import GroupSelect from 'app/components/group/GroupSelect';
import EditGroupDialog from 'app/components/group/EditGroupDialog';
import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({

    navigatorContainer: {
        height: '100%',
    },
    gridContainer: {
        maxWidth: '100%',
        height: '100%',
        flexWrap: 'nowrap'
    },
    gridContainerEmpty: {
        height: '100%',
        width: '100',
        alignItems: 'center',
        justifyContent: 'center'
    },
    gridItemTitle: {
        padding: theme.spacing(2),
        borderBottom: '1px solid ' + theme.palette.grey[200],
    },
    gridItemSearch: {
        padding: theme.spacing(2),
        borderBottom: '1px solid ' + theme.palette.grey[200],
    },
    gridItemAction: {
        padding: theme.spacing(2),
        borderTop: '1px solid ' + theme.palette.grey[200],
    },
    gridItemList: {
        flexGrow: 1,
        overflow: 'scroll',
        position: 'relative',
    },
    borderRight: {
        borderRight: '1px solid ' + theme.palette.grey[200],
    },
    button: {
        padding: '15px 24px',
    },
    leftIcon: {
        marginRight: theme.spacing(1),
    },
    rightIcon: {
        marginLeft: theme.spacing(1),
    },
    textButton: {
        marginLeft: theme.spacing(2),
        cursor: 'pointer'
    },
    flexGrow: {
        flexGrow: 1
    },
    marginBottom: {
        marginBottom: theme.spacing(2),
    }
}));

const createItemData = memoize((items, checkedItems = [], toggleItem = false, selectedItems = []) => ({
  items,
  checkedItems,
  toggleItem,
  selectedItems
}));

const Row = memo(({ data, index, style }) => {

    const user = data.items[index];
    const checkedUsers = data.checkedItems;
    const selectedUsers = data.selectedItems;
    const toggleUser = data.toggleItem

    return (
        <ListItem
            style={style}
            key={index}
            button
            onClick={() => toggleUser(user)}
            disabled={selectedUsers.findIndex(selectedUser => selectedUser.id === user.id) > -1}
            >
            <ListItemIcon>
                <Checkbox
                    edge="start"
                    checked={(checkedUsers.indexOf(user) !== -1)}
                    disableRipple
                    color="primary"
                />
            </ListItemIcon>
            <ListItemText primary={user.full_name} />
        </ListItem>
    );
});

export default function GroupNavigator(props) {

    const [ groups, setGroups ] = useState([]);
    const [ activeGroupIndex, setActiveGroupIndex ] = useState(-1);

    const [ users, setUsers ] = useState([]);

    const [ filteredUsers, setFilteredUsers ] = useState([]);
    const [ checkedUsers, setCheckedUsers ] = useState([]);
    const [ userView, setUserView ] = useState('all');

    const [ filteredGroupUsers, setFilteredGroupUsers ] = useState([]);
    const [ checkedGroupUsers, setCheckedGroupUsers ] = useState([]);
    const [ groupView, setGroupView ] = useState('all');

    const [ groupSearchValue, setGroupSearchValue ] = useState("");
    const [ userSearchValue, setUserSearchValue ] = useState("");

    const [ groupsLoading, setGroupsLoading ] = useState(false);
    const [ usersInGroupLoading, setUsersInGroupLoading ] = useState(false);
    const [ usersLoading, setUsersLoading ] = useState(false);
    const [ errors, setErrors ] = useState({})

    const [ groupDialogOpen, setGroupDialogOpen ] = useState(false);
    const [ editingGroup, setEditingGroup ] = useState(false);

    const { variant, title, singular } = props;

    const classes = useStyles();
    const api = useApi();

    const getGroups = () => {
        setGroupsLoading(true);

        api.get(`/admin/group/${variant}/users`)
            .then(res => {
                setGroups(res.groups);
            }).catch( error => {
                //console.log(error);
            }).then(() => setGroupsLoading(false));
    }

    const getUsers = () => {
        setUsersLoading(true);

        api.get(`/admin/group/user`)
            .then(res => {
                setUsers(res.users);
                setFilteredUsers(res.users);
            }).catch( error => {
                //console.log(error);
            }).then(() => setUsersLoading(false));
    }

    useEffect(() => {
        getUsers();
        getGroups();
    }, []);

    // Group Column Functions ///////////////////////////////////////////////////////////////

    const toggleGroup = id => {

        const newIndex = groups.findIndex(group => group.id === id);
        setActiveGroupIndex(newIndex);
        setFilteredGroupUsers((newIndex === -1) ? [] : groups[newIndex].users);
        setCheckedGroupUsers([]);
        syncGroupUsersWithCheckedUsers(newIndex);
    }

    const syncGroupUsersWithCheckedUsers = groupIndex => {

        const newChecked = [...checkedUsers];

        if (checkedUsers.length && groups[groupIndex].users.length) {

            checkedUsers.forEach(user => {
                const i = groups[groupIndex].users.findIndex(groupUser => groupUser.id === user.id);

                if (i > -1) newChecked.splice(i, 1);
            })
        }

        if (checkedGroupUsers.length) newChecked.push(...checkedGroupUsers);

        setCheckedUsers(newChecked);

        if (newChecked.length) {
            setUserView('selected');
            setFilteredUsers([...newChecked]);
            setUserSearchValue("");
        } else {
            setUserView('all');
        }
    }

    const addOldGroupUsersToCheckedUsers = groupIndex => {

        if (groupIndex > -1 && groups[groupIndex].users.length) {
            const newChecked = [...checkedUsers];

            groups[groupIndex].users.forEach( user => {
                const i = newChecked.findIndex(checkedUser => checkedUser.id === user.id)
                if (i > -1) {
                    newChecked.splice(i, 1);
                }
            })
            setCheckedUsers(newChecked)
        }
    }

    // Group User Column Functions ///////////////////////////////////////////////////////////////

    const handleGroupSearchValue = event => {
        setGroupSearchValue(event.target.value);
    };

    const handleGroupSearch = (event, bypass = false) => {

        if (bypass || event.key === 'Enter') {

            event.preventDefault();

            const filteringUsers = (groupView == 'all') ? groups[activeGroupIndex].users : checkedGroupUsers;

            let filteredOptions = filteringUsers.filter((u) => {
                let match = false;
                if (u.full_name !== null) {
                    let m = u.full_name.toLowerCase().includes(groupSearchValue.toLowerCase());
                    if (m) match = true;
                }
                return match;
            })

            setFilteredGroupUsers( filteredOptions );
        }
    };

    const clearGroupSearch = () => {
        setGroupSearchValue("");
        if (groupView == 'all') {
            setFilteredGroupUsers([...groups[activeGroupIndex].users]);
        } else {
            setFilteredGroupUsers([...checkedGroupUsers]);
        }
    }

    const toggleGroupView = () => {
        if (groupView == 'all') {
            setGroupView('selected');
            setFilteredGroupUsers([...checkedGroupUsers]);
            setGroupSearchValue("");
        } else {
            setGroupView('all');
            setFilteredGroupUsers([...groups[activeGroupIndex].users]);
            setUserSearchValue("");
        }
    }

    const addToCheckedGroupUsers = user => {
        const currentIndex = checkedGroupUsers.findIndex(cgu => cgu.id === user.id);
        const newChecked = [...checkedGroupUsers];

        if (currentIndex === -1) {
            newChecked.push(user)
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setCheckedGroupUsers(newChecked);
    }

    const toggleAllGroupUsers = () => {
        if (checkedGroupUsers.length == groups[activeGroupIndex].users.length) {
            setCheckedGroupUsers([]);
        } else {
            setCheckedGroupUsers([...groups[activeGroupIndex].users]);
        }
    }

    const removeCheckedGroupUsers = () => {

        const data = {
            users: checkedGroupUsers,
            type: 'remove'
        }

        api.post('/admin/group/' + variant + '/' + groups[activeGroupIndex].id + '/syncUsers', data)
            .then(res => {
                getGroups();
                setCheckedGroupUsers([]);
            }).finally(() => setUsersInGroupLoading(false));
    }

    useEffect(() => {
        if (activeGroupIndex > -1) {
            clearGroupSearch();
        }
    }, [groups]);

    // User Column Functions ///////////////////////////////////////////////////////////////

    const handleUserSearchValue = event => {
        setUserSearchValue(event.target.value);
    };

    const handleUserSearch = (event, bypass = false) => {

        if (bypass || event.key === 'Enter') {

            event.preventDefault();

            const filteringUsers = (userView == 'all') ? users : checkedUsers;

            let filteredOptions = filteringUsers.filter((u) => {
                let match = false;
                if (u.full_name !== null) {
                    let m = u.full_name.toLowerCase().includes(userSearchValue.toLowerCase());
                    if (m) match = true;
                }
                return match;
            })

            setFilteredUsers( filteredOptions );
        }
    };

    const clearUserSearch = () => {
        setUserSearchValue("");
        if (userView == 'all') {
            setFilteredUsers([...users]);
        } else {
            setFilteredUsers([...checkedUsers]);
        }
    }

    const toggleUserView = () => {
        if (userView == 'all') {
            setUserView('selected');
            setFilteredUsers([...checkedUsers]);
            setUserSearchValue("");
        } else {
            setUserView('all');
            setFilteredUsers([...users]);
            setUserSearchValue("");
        }
    }

    const addToCheckedUsers = user => {
        const currentIndex = checkedUsers.indexOf(user);
        const newChecked = [...checkedUsers];

        if (currentIndex === -1) {
            newChecked.push(user)
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setCheckedUsers(newChecked);
    }

    const toggleAllUsers = () => {
        if (checkedUsers.length == users.length) {
            setCheckedUsers([]);
        } else {
            setCheckedUsers([...users]);
        }
    }

    const addCheckedUsersToGroup = () => {
        setUsersInGroupLoading(true);

        const data = {
            users: checkedUsers,
            type: 'add'
        }

        api.post('/admin/group/' + variant + '/' + groups[activeGroupIndex].id + '/syncUsers', data)
            .then(res => {
                getGroups();
                clearGroupSearch();
                setCheckedUsers([]);
            }).finally(() => setUsersInGroupLoading(false));
    }

    // Editing Dialog Functions ///////////////////////////////////////////////////////////////

    const openGroupDialog = (id = 0) => {
        if (id > 0) {
            const group = groups.find(group => group.id === id);
            setEditingGroup(group);
        }

        setGroupDialogOpen(true)
    }

    const closeGroupDialog = () => {
        setGroupDialogOpen(false);
    }

    const closeGroupDialogAndRefresh = () => {
        closeGroupDialog();
        getGroups();
    }

    const groupDialogClosed = () => {
        setEditingGroup(false);
    }

    const closeGroupDialogAndRefreshWithToggleCheck = () => {
        if (editingGroup && (activeGroupIndex > -1) && (editingGroup.id == groups[activeGroupIndex].id)) {
            toggleGroup(-1);
        }
        closeGroupDialogAndRefresh();
    }

    // Render Based Functions

    const groupUsers = (activeGroupIndex > -1) ? groups[activeGroupIndex].users : [];

    const userListRowData = createItemData(filteredUsers, checkedUsers, addToCheckedUsers, groupUsers);
    const groupListRowData = (activeGroupIndex > -1) ? createItemData(filteredGroupUsers, checkedGroupUsers, addToCheckedGroupUsers) : [];

    let groupUserList;

    if (activeGroupIndex == -1) {
        groupUserList = (
            <Grid container classes={{ container: classes.gridContainerEmpty }}>
                <Grid item>
                    <Typography variant="subtitle2" color="textSecondary">
                        Select a {variant} to view its users.
                    </Typography>
                </Grid>
            </Grid>
        )
    } else if (groups[activeGroupIndex].users.length == 0) {
        groupUserList = (
            <Grid container classes={{ container: classes.gridContainerEmpty }}>
                <Grid item>
                    <Typography variant="subtitle2" color="textSecondary">
                        This {variant} has no users.
                    </Typography>
                </Grid>
            </Grid>
        )
    } else if (filteredGroupUsers.length == 0) {
        groupUserList = (
            <Grid container classes={{ container: classes.gridContainerEmpty }}>
                <Grid item>
                    <Typography variant="subtitle2" color="textSecondary">
                        No matching users in this {variant}.
                    </Typography>
                </Grid>
            </Grid>
        )
    } else {
        groupUserList = (
            <AutoSizer>
                {({ height, width }) => (
                    <FixedSizeList
                        height={height}
                        width={width}
                        itemSize={36}
                        itemCount={filteredGroupUsers.length}
                        itemData={groupListRowData}
                        >
                        {Row}
                    </FixedSizeList>
                )}
            </AutoSizer>
        )
    }

    return (
        <div className={classes.navigatorContainer}>
            <Grid container classes={{ container: classes.gridContainer }}>
                <Grid item xs={4} classes={{ item: classes.borderRight }}>
                    <Grid container direction="column" classes={{ container: classes.gridContainer }}>
                        <Grid item classes={{ item: classes.gridItemTitle }}>
                            <Typography variant="h6" component="h3">{title}</Typography>
                        </Grid>
                        <Grid item classes={{ item: classes.gridItemSearch }}>
                            <Button
                                fullWidth
                                color="secondary"
                                variant="outlined"
                                aria-label="Add New Group"
                                className={classes.button}
                                onClick={openGroupDialog}
                                >
                                <AddIcon className={classes.leftIcon} />
                                Create A {singular}
                            </Button>
                        </Grid>
                        <Grid item classes={{ item: classes.gridItemList }}>
                            <GroupSelect
                                variant={variant}
                                onSelect={toggleGroup}
                                enableEdit
                                onEdit={openGroupDialog}
                                groupRefresh={groups}
                                unselectableId={0}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={4} classes={{ item: classes.borderRight }}>
                    <Grid container direction="column" classes={{ container: classes.gridContainer }}>
                        <Grid item classes={{ item: classes.gridItemTitle }}>
                            <Typography variant="h6" component="h3">Users in {singular}</Typography>
                        </Grid>
                        <Grid item classes={{ item: classes.gridItemSearch }}>
                            <Grid container alignItems="center">
                                <Grid item classes={{ item: classes.flexGrow }}>
                                    <TextField
                                        fullWidth
                                        id="outlined-search"
                                        label={"Search " + singular}
                                        value={groupSearchValue}
                                        onChange={handleGroupSearchValue}
                                        margin="none"
                                        variant="outlined"
                                        type="search"
                                        onKeyPress={handleGroupSearch}
                                        />
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        aria-label="Search"
                                        variant="contained"
                                        className={classes.rightIcon}
                                        disabled={(groupSearchValue.length == 0)}
                                        onClick={(event) => handleGroupSearch(event, true)}
                                        >
                                        <SearchIcon/>
                                    </IconButton>
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        aria-label="Clear"
                                        variant="contained"
                                        disabled={(groupSearchValue.length == 0)}
                                        onClick={clearGroupSearch}
                                        >
                                        <ClearIcon/>
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item classes={{ item: classes.gridItemList }}>
                            <Loading isLoading={usersInGroupLoading}/>
                            {groupUserList}
                        </Grid>
                        <Grid item classes={{ item: classes.gridItemAction }}>
                            <Typography variant="subtitle2" component="div" className={classes.marginBottom}>
                                Selected: {checkedGroupUsers.length}
                                <Link onClick={toggleGroupView} className={classes.textButton}>
                                    {groupView == 'all' ? 'View Selected Users' : 'View All Users in ' + singular}
                                </Link>
                            </Typography>
                            <ButtonGroup fullWidth color="primary">
                                <Button
                                    disabled={(groupUsers.length === 0)}
                                    onClick={toggleAllGroupUsers}
                                    >
                                    {(groupUsers.length > 0 && checkedGroupUsers.length == groupUsers.length) ? 'Unselect All' : 'Select All'
                                    }
                                </Button>
                                <Button
                                    disabled={(checkedGroupUsers.length === 0)}
                                    onClick={removeCheckedGroupUsers}
                                    >
                                    Remove
                                </Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={4}>
                    <Grid container direction="column" classes={{ container: classes.gridContainer }}>
                        <Grid item classes={{ item: classes.gridItemTitle }}>
                            <Typography variant="h6" component="h3">Available Users</Typography>
                        </Grid>
                        <Grid item classes={{ item: classes.gridItemSearch }}>
                            <Grid container alignItems="center">
                                <Grid item classes={{ item: classes.flexGrow }}>
                                    <TextField
                                        fullWidth
                                        id="outlined-search"
                                        label="Search Users"
                                        value={userSearchValue}
                                        onChange={handleUserSearchValue}
                                        margin="none"
                                        variant="outlined"
                                        type="search"
                                        onKeyPress={handleUserSearch}
                                        />
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        aria-label="Search"
                                        variant="contained"
                                        className={classes.rightIcon}
                                        disabled={(userSearchValue.length == 0)}
                                        onClick={(event) => handleUserSearch(event, true)}
                                        >
                                        <SearchIcon/>
                                    </IconButton>
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        aria-label="Clear"
                                        variant="contained"
                                        disabled={(userSearchValue.length == 0)}
                                        onClick={clearUserSearch}
                                        >
                                        <ClearIcon/>
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item classes={{ item: classes.gridItemList }}>
                            <Loading isLoading={usersLoading}/>
                            {(filteredUsers.length == 0) ? (
                                <Grid container classes={{ container: classes.gridContainerEmpty }}>
                                    <Grid item>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            No Users Available.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ) : (
                                <AutoSizer>
                                    {({ height, width }) => (
                                        <FixedSizeList
                                            height={height}
                                            width={width}
                                            itemSize={36}
                                            itemCount={filteredUsers.length}
                                            itemData={userListRowData}
                                            >
                                            {Row}
                                        </FixedSizeList>
                                    )}
                                </AutoSizer>
                            )}
                        </Grid>
                        <Grid item classes={{ item: classes.gridItemAction }}>
                            <Typography
                                variant="subtitle2"
                                component="div"
                                className={classes.marginBottom}
                                >
                                Selected: {checkedUsers.length}
                                <Link onClick={toggleUserView} className={classes.textButton}>
                                    {userView == 'all' ? 'View Selected Users' : 'View All Users'}
                                </Link>
                            </Typography>
                            <ButtonGroup fullWidth color="primary">
                                <Button onClick={toggleAllUsers}>
                                    {(checkedUsers.length == users.length) ? 'Unselect All' : 'Select All'}
                                </Button>
                                <Button
                                    disabled={(checkedUsers.length === 0 || activeGroupIndex === -1)}
                                    onClick={addCheckedUsersToGroup}
                                    >
                                    Add
                                </Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <EditGroupDialog
                variant={variant}
                open={groupDialogOpen}
                group={editingGroup}
                groups={groups}
                onSave={closeGroupDialogAndRefresh}
                onDelete={closeGroupDialogAndRefreshWithToggleCheck}
                onClose={closeGroupDialog}
                onClosed={groupDialogClosed}
                singular={singular}
                />
        </div>
    );
}
